// src/App.js
// Version: 2.1.0 - Added navigation system with Home, Shindan, Privacy Policy routes

import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

import { auth, db, APP_ID } from './firebase/config';

import { AuthContext } from './contexts/AuthContext';
import { LanguageContext } from './contexts/LanguageContext';
import { NotificationContext } from './contexts/NotificationContext';

import Spinner from './components/common/Spinner';
import NotificationComponent from './components/common/Notification';
import Navigation from './components/common/Navigation';
import Footer from './components/common/Footer';

import { initialQuestionnaireSets } from './i18n/initialData';

// Component to conditionally render Footer
const ConditionalFooter = () => {
  const location = useLocation();
  // Don't render Footer on HomePage (/) since it has its own custom footer
  if (location.pathname === '/') {
    return null;
  }
  return <Footer />;
};

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/Home.v2'));
const ShindanPage = React.lazy(() => import('./pages/ChatbotPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicy'));
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));
const AdminPanelPage = React.lazy(() => import('./pages/AdminPanelPage'));
const ResourceDownloadPage = React.lazy(() => import('./pages/ResourceDownloadPage'));

function App() {
  console.log("App: Component rendering START");

  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState('ja');
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    console.log("App: Auth listener effect setup (runs once)");
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("App: onAuthStateChanged triggered. User:", currentUser, "Current isAuthLoading:", isAuthLoading);
      setUser(currentUser);
      if (isAuthLoading) { // Only set to false once
        setIsAuthLoading(false);
        console.log("App: Auth loading finished.");
      }
    });
    return () => {
      console.log("App: Auth listener cleanup");
      unsubscribe();
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  const showNotification = useCallback((message, type = 'info') => {
    console.log("App: showNotification called", message, type);
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null); 
    }, 5000);
  }, []); 

  const hideNotification = useCallback(() => {
    console.log("App: hideNotification called");
    setNotification(null);
  }, []);

  useEffect(() => {
    console.log("App: Initial data effect triggered. isAuthLoading:", isAuthLoading, "APP_ID:", APP_ID);
    if (isAuthLoading) {
      console.log("App: Initial data effect - returning because auth is still loading.");
      return;
    }
    if (!db || !APP_ID || APP_ID === 'interactive-questionnaire-fallback') {
      console.error("App: Firebase DB or APP_ID is not properly configured for initial data setup. APP_ID:", APP_ID);
      showNotification("Critical: App configuration error. Cannot load initial data.", "error");
      return;
    }

    let isMounted = true; // To prevent state updates on unmounted component

    const initializeDefaultData = async () => {
      console.log("App: initializeDefaultData running...");
      try {
        const setsCollectionPath = `artifacts/${APP_ID}/public/data/questionnaireSets`;
        const setsCollectionRef = collection(db, setsCollectionPath);
        const snapshot = await getDocs(setsCollectionRef);
        
        if (isMounted && snapshot.empty) {
          console.log(`App: QuestionnaireSets collection at ${setsCollectionPath} is empty. Initializing...`);
          for (const set of initialQuestionnaireSets) {
            const setToAdd = { ...set };
            if (set.createdAt === 'serverTimestampPlaceholder') {
              setToAdd.createdAt = serverTimestamp();
            }
            if (!Array.isArray(setToAdd.questions)) {
                setToAdd.questions = [];
            }
            await addDoc(setsCollectionRef, setToAdd);
          }
          console.log("App: Initial questionnaire sets added to Firestore.");
        } else if (isMounted) {
          console.log("App: QuestionnaireSets collection is NOT empty or component unmounted.");
        }
      } catch (error) {
        console.error("App: Error initializing Firestore data:", error);
        if (isMounted) {
          showNotification(`Error initializing data: ${error.message}`, "error");
        }
      }
    };

    initializeDefaultData();
    
    return () => {
      isMounted = false;
      console.log("App: Initial data effect cleanup.");
    };
  }, [isAuthLoading, APP_ID, db, showNotification]);

  const handleLogout = useCallback(async (showErrorCallback) => {
    console.log("App: handleLogout called");
    try {
      await signOut(auth);
      setUser(null); // Auth state change will trigger re-renders and ProtectedRoute logic
    } catch (error) {
      console.error("App: Logout error:", error);
      const errorMessage = "Error logging out: " + error.message;
      if (showErrorCallback && typeof showErrorCallback === 'function') {
        showErrorCallback(errorMessage, 'error');
      } else {
        showNotification(errorMessage, 'error');
      }
    }
  }, [showNotification]);

  if (isAuthLoading) {
    console.log("App: Rendering loading spinner (isAuthLoading is true)");
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <Spinner />
        <p className="ml-2">Authenticating...</p>
      </div>
    );
  }

  console.log("App: Proceeding to render Router. User:", user ? user.uid : "null");
  return (
    <AuthContext.Provider value={{ user, setUser, logout: handleLogout, isAuthLoading }}>
      <LanguageContext.Provider value={{ lang: currentLanguage, setLang: setCurrentLanguage }}>
        <NotificationContext.Provider value={{ notification, showNotification, hideNotification }}>
          <Router>
            <div className="min-h-screen bg-gray-50 pt-16 font-sans flex flex-col">
              <Navigation />
              <NotificationComponent />
              <main className="flex flex-1">
                <Suspense fallback={<div className="flex justify-center items-center h-screen"><Spinner /><p className="ml-2">Loading page...</p></div>}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/home" element={<Navigate to="/" replace />} />
                    <Route path="/shindan" element={<ShindanPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/login" element={<AdminLoginPage />} />
                    <Route path="/whitepaper" element={<ResourceDownloadPage />} />
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
                          <AdminPanelPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </main>
              <ConditionalFooter />
            </div>
          </Router>
        </NotificationContext.Provider>
      </LanguageContext.Provider>
    </AuthContext.Provider>
  );
}

const ProtectedRoute = ({ user, isAuthLoading, children }) => {
  console.log("ProtectedRoute: Rendering. isAuthLoading:", isAuthLoading, "User:", user ? user.uid : "null");
  const location = useLocation();

  if (isAuthLoading) {
    console.log("ProtectedRoute: Showing spinner (auth still loading)");
    return <div className="flex justify-center items-center h-screen"><Spinner /><p className="ml-2">Checking authentication...</p></div>;
  }

  if (!user) {
    console.log("ProtectedRoute: No user, redirecting to login. From:", location.pathname);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  console.log("ProtectedRoute: User authenticated, rendering children.");
  return children;
};

export default App;